import React, { useEffect, useMemo, useState } from 'react'

import ContextSelectorBar from '../components/ContextSelectorBar'
import GradientButton from '../components/GradientButton'
import { useLots } from '../hooks/useLots'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import { useT } from '../i18n'
import type { Lot } from '../types/lot'
import { loadActiveContextSelection } from '../utils/activeContextSelection'
import {
  capacityAssignmentFor,
  readCapacityTables,
  readDefaultCapacityTable,
  readLotCapacityAssignment,
  withCapacityTables,
  withLotCapacityAssignment,
  type CarryingCapacityTable
} from '../utils/carryingCapacity'
import { findFarmByIdOrName } from '../utils/contextSelection'
import { saveEditedFarm } from '../utils/editFarmApi'
import { farmDayKey, farmTimeZone } from '../utils/farmTime'
import { ensureFarmList, readCurrentFarm, type WizardFarm } from '../utils/farmWizard'
import { createId } from '../utils/id'
import { lotApi } from '../utils/lotApi'
import CapacityTableEditor, { parseCapacityDraft, toCapacityDraft, type CapacityDraft } from './capacity/CapacityTableEditor'

// Configuración → Capacidad de carga (findings #151): the facility's named
// tables of maximum density and minimum water flow. A lot keeps a copy of the
// table it was given (Lotes), so editing a table here changes new lots only,
// unless "Aplicar también a esos lotes" is pressed.

const NEW_TABLE_ID = '__new__'

const isActiveLot = (lot: Lot): boolean => (!lot.stage || lot.stage === 'Active') && !(lot as { deletedAt?: string }).deletedAt

type UsageGroup = { rootId: string; lotKey: string; units: string[]; assignedAt: string; lots: Lot[] }

export default function CarryingCapacity() {
  const t = useT()
  const [farms, setFarms] = useState<WizardFarm[]>([])
  const [farmId, setFarmId] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [draft, setDraft] = useState<CapacityDraft | null>(null)
  const [savedDraftKey, setSavedDraftKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const deleteConfirm = useTwoStepConfirm()

  useEffect(() => {
    let cancelled = false
    void ensureFarmList().then((list) => {
      if (cancelled) return
      setFarms(list)
      const active = loadActiveContextSelection()
      const remembered = readCurrentFarm()
      const farm = findFarmByIdOrName(list, active.farmId) ?? findFarmByIdOrName(list, remembered?.id ?? remembered?.name ?? '') ?? list[0]
      setFarmId(farm?.id ? String(farm.id) : '')
    })
    return () => {
      cancelled = true
    }
  }, [])

  const farm = useMemo(() => farms.find((candidate) => String(candidate.id) === farmId) ?? null, [farms, farmId])
  const tables = useMemo(() => readCapacityTables(farm), [farm])
  const defaultTable = useMemo(() => readDefaultCapacityTable(farm), [farm])
  const { lots, refresh: refreshLots } = useLots({ farmId, enabled: Boolean(farmId) })

  const selectedTable = tables.find((table) => table.id === selectedId) ?? null

  const openTable = (table: CarryingCapacityTable | null, id: string, nextDraft?: CapacityDraft) => {
    const value = nextDraft ?? (table ? toCapacityDraft(table) : null)
    setSelectedId(id)
    setDraft(value)
    setSavedDraftKey(table && !nextDraft ? JSON.stringify(value) : '')
    deleteConfirm.reset()
  }

  // Open the default (or first) table when the facility changes.
  useEffect(() => {
    const first = defaultTable ?? tables[0] ?? null
    openTable(first, first?.id ?? '')
    setStatus(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, farm === null])

  const unitName = (unitId: string | null | undefined): string => {
    const unit = (farm?.productionUnits ?? []).find((candidate) => String(candidate.id) === String(unitId ?? ''))
    return unit?.name || String(unitId ?? '')
  }

  const usage = useMemo((): { groups: UsageGroup[]; withoutCopy: number } => {
    if (!selectedTable) return { groups: [], withoutCopy: 0 }
    const groups = new Map<string, UsageGroup>()
    let withoutCopy = 0
    for (const lot of lots) {
      if (!isActiveLot(lot) || !lot.productionUnitId) continue
      const assignment = readLotCapacityAssignment(lot)
      if (!assignment) {
        if (defaultTable?.id === selectedTable.id) withoutCopy += 1
        continue
      }
      if (assignment.tableId !== selectedTable.id) continue
      const rootId = lot.lineageRootId ?? lot.id
      const group = groups.get(rootId) ?? { rootId, lotKey: lot.lotKey, units: [], assignedAt: assignment.assignedAt, lots: [] }
      group.units.push(unitName(lot.productionUnitId))
      group.lots.push(lot)
      groups.set(rootId, group)
    }
    return {
      groups: [...groups.values()].map((group) => ({ ...group, units: group.units.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })) })),
      withoutCopy
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lots, selectedTable, defaultTable, farm])

  const dirty = draft !== null && JSON.stringify(draft) !== savedDraftKey

  const saveFarm = async (nextTables: CarryingCapacityTable[], nextDefaultId: string | null): Promise<boolean> => {
    if (!farm) return false
    const nextFarm = withCapacityTables(farm, nextTables, nextDefaultId)
    setBusy(true)
    try {
      const result = await saveEditedFarm(nextFarm, farm)
      setFarms((prev) => prev.map((candidate) => (String(candidate.id) === String(farm.id) ? result.data : candidate)))
      return true
    } catch (error) {
      console.warn('[capacity] save failed', error)
      setStatus({ tone: 'error', text: t('capacity.status.saveFailed') })
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleSave = async () => {
    if (!draft) return
    const parsed = parseCapacityDraft(draft)
    if (!parsed.ok) {
      setStatus({ tone: 'error', text: t(parsed.errorKey) })
      return
    }
    if (tables.some((table) => table.id !== selectedId && table.name.toLocaleLowerCase() === parsed.value.name.toLocaleLowerCase())) {
      setStatus({ tone: 'error', text: t('capacity.error.nameTaken') })
      return
    }
    const id = selectedId === NEW_TABLE_ID || !selectedId ? createId('capacity') : selectedId
    const saved: CarryingCapacityTable = { id, ...parsed.value, updatedAt: new Date().toISOString() }
    const nextTables = [...tables.filter((table) => table.id !== id), saved]
    const nextDefault = defaultTable?.id ?? (nextTables.length === 1 ? id : null)
    if (await saveFarm(nextTables, nextDefault)) {
      openTable(saved, id)
      setStatus({ tone: 'ok', text: t('capacity.status.saved', { name: saved.name }) })
    }
  }

  const handleNew = () => {
    openTable(null, NEW_TABLE_ID, toCapacityDraft({ name: '', bands: [], flowTemperaturesC: [], hatcheryMinLpmPerThousand: null }))
    setStatus(null)
  }

  const handleDuplicate = () => {
    if (!selectedTable) return
    openTable(null, NEW_TABLE_ID, { ...toCapacityDraft(selectedTable), name: t('capacity.copyName', { name: selectedTable.name }) })
    setStatus(null)
  }

  const handleMakeDefault = async () => {
    if (!selectedTable) return
    if (await saveFarm(tables, selectedTable.id)) setStatus({ tone: 'ok', text: t('capacity.status.default', { name: selectedTable.name }) })
  }

  const handleDelete = async () => {
    if (!selectedTable) return
    if (!deleteConfirm.isPending(selectedTable.id)) {
      deleteConfirm.arm(selectedTable.id)
      return
    }
    deleteConfirm.reset()
    const nextTables = tables.filter((table) => table.id !== selectedTable.id)
    if (await saveFarm(nextTables, defaultTable?.id ?? null)) {
      const next = nextTables[0] ?? null
      openTable(next, next?.id ?? '')
      setStatus({ tone: 'ok', text: t('capacity.status.deleted', { name: selectedTable.name }) })
    }
  }

  // Re-copy the saved table into the active unit lots that use it.
  const handleApplyToLots = async () => {
    if (!selectedTable || usage.groups.length === 0) return
    const assignment = capacityAssignmentFor(selectedTable, new Date().toISOString())
    const targets = new Map<string, Lot>()
    for (const group of usage.groups) {
      for (const lot of group.lots) targets.set(lot.id, lot)
    }
    setBusy(true)
    try {
      for (const lot of targets.values()) {
        await lotApi.update(lot.id, { metadata: withLotCapacityAssignment(lot, assignment).metadata })
      }
      await refreshLots?.()
      setStatus({ tone: 'ok', text: t('capacity.status.applied', { count: usage.groups.length }) })
    } catch (error) {
      console.warn('[capacity] apply to lots failed', error)
      setStatus({ tone: 'error', text: t('capacity.status.saveFailed') })
    } finally {
      setBusy(false)
    }
  }

  const dayLabel = (iso: string): string => {
    const day = farmDayKey(iso, farmTimeZone(farm as Parameters<typeof farmTimeZone>[0])) ?? iso.slice(0, 10)
    const [year, month, date] = day.split('-')
    return year && month && date ? `${date}/${month}/${year}` : day
  }
  const isNew = selectedId === NEW_TABLE_ID
  const used = usage.groups.length > 0
  const isDefault = Boolean(selectedTable && defaultTable?.id === selectedTable.id)

  return (
    <div data-screen="carrying-capacity" className="responsive-container">
      <div className="app-header" style={{ padding: 0 }}>
        <h1>⚖️ {t('capacity.title')}</h1>
        <p>{t('capacity.subtitle')}</p>
      </div>

      <div className="form-card" style={{ marginTop: 14 }}>
        <ContextSelectorBar
          farm={{
            label: t('warehouse.contextFacility'),
            value: farmId,
            options: farms.map((candidate) => ({ value: String(candidate.id), label: candidate.name || String(candidate.id), key: String(candidate.id) })),
            onChange: setFarmId,
            placeholder: farms.length === 0 ? t('warehouse.loadingFacilities') : t('warehouse.selectFacility'),
            disabled: farms.length === 0 || busy
          }}
        />
      </div>

      {farm ? (
        <div className="form-card" style={{ marginTop: 14, display: 'grid', gap: 12 }} data-testid="capacity-tables">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontWeight: 600, color: '#475569' }}>
              {t('capacity.table')}
              <select
                data-testid="capacity-table-select"
                value={selectedId}
                onChange={(event) => {
                  const table = tables.find((candidate) => candidate.id === event.target.value) ?? null
                  openTable(table, event.target.value)
                  setStatus(null)
                }}
                disabled={busy}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5f5' }}
              >
                {tables.length === 0 && !isNew ? <option value="">{t('capacity.noTables')}</option> : null}
                {tables.map((table) => (
                  <option key={table.id} value={table.id}>
                    {table.name}{defaultTable?.id === table.id ? ` ★ ${t('capacity.default')}` : ''}
                  </option>
                ))}
                {isNew ? <option value={NEW_TABLE_ID}>{t('capacity.newTableOption')}</option> : null}
              </select>
            </label>
            {isDefault ? <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>★ {t('capacity.defaultBadge')}</span> : null}
            <GradientButton size="xs" variant="secondary" onClick={handleNew} disabled={busy} data-testid="capacity-new">{t('capacity.action.new')}</GradientButton>
            <GradientButton size="xs" variant="secondary" onClick={handleDuplicate} disabled={busy || !selectedTable} data-testid="capacity-duplicate">{t('capacity.action.duplicate')}</GradientButton>
            {selectedTable && !isDefault ? (
              <GradientButton size="xs" variant="secondary" onClick={() => void handleMakeDefault()} disabled={busy} data-testid="capacity-make-default">{t('capacity.action.makeDefault')}</GradientButton>
            ) : null}
            {selectedTable && !isDefault && !used ? (
              <GradientButton size="xs" variant="danger" onClick={() => void handleDelete()} disabled={busy} data-testid="capacity-delete">
                {deleteConfirm.isPending(selectedTable.id) ? t('capacity.action.deleteConfirm', { name: selectedTable.name }) : t('capacity.action.delete')}
              </GradientButton>
            ) : null}
          </div>

          {selectedTable ? (
            <div style={{ fontSize: 13, color: '#475569' }} data-testid="capacity-usage">
              {used
                ? t('capacity.usedBy', {
                  lots: usage.groups.map((group) => t('capacity.usedByLot', {
                    lot: group.lotKey,
                    units: group.units.join(', '),
                    date: dayLabel(group.assignedAt)
                  })).join(' · ')
                })
                : t('capacity.unused')}
              {usage.withoutCopy > 0 ? ` ${t('capacity.withoutCopy', { count: usage.withoutCopy })}` : ''}
            </div>
          ) : null}

          {draft ? (
            <>
              <CapacityTableEditor draft={draft} onChange={(update) => setDraft((current) => (current ? update(current) : current))} disabled={busy} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <GradientButton size="sm" variant="success" onClick={() => void handleSave()} disabled={busy || !dirty} data-testid="capacity-save">
                  {t('common.save')}
                </GradientButton>
                {used && !dirty ? (
                  <GradientButton size="sm" variant="secondary" onClick={() => void handleApplyToLots()} disabled={busy} data-testid="capacity-apply-lots">
                    {t('capacity.action.applyToLots', { count: usage.groups.length })}
                  </GradientButton>
                ) : null}
              </div>
              {used ? <span style={{ fontSize: 12, color: '#6b7280' }}>{t('capacity.copiesNote')}</span> : null}
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#475569' }}>{t('capacity.empty')}</span>
          )}

          {status ? (
            <div role={status.tone === 'error' ? 'alert' : 'status'} style={{ fontSize: 13, fontWeight: 600, color: status.tone === 'error' ? '#b91c1c' : '#15803d' }}>
              {status.text}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
